/**
 * Generic URL query-string helpers. These are intentionally business-logic
 * agnostic — they know how to serialize/deserialize plain values, arrays and
 * nested key/value records, but they do not know anything about the UBP
 * domain model.
 */

export type Primitive = string | number | boolean;

export type QueryValue = Primitive | Primitive[] | Record<string, Primitive> | undefined | null;

/**
 * Build a URL-encoded query string from an arbitrary object of values.
 *
 * - Primitive values are serialized as `key=value`.
 * - Arrays of primitives are serialized as `key[]=v1&key[]=v2`.
 * - Records of primitives are flattened to `key.subKey=value`.
 * - `undefined` and `null` values are omitted.
 *
 * The returned string does NOT include a leading `?`.
 */
export const toQueryString = (values: Record<string, QueryValue>): string => {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(values)) {
		if (value === undefined || value === null) continue;
		if (Array.isArray(value)) {
			for (const v of value) {
				params.append(`${key}[]`, String(v));
			}
		} else if (typeof value === 'object') {
			for (const [subKey, subValue] of Object.entries(value)) {
				if (subValue === undefined || subValue === null) continue;
				params.append(`${key}.${subKey}`, String(subValue));
			}
		} else {
			params.append(key, String(value));
		}
	}
	return params.toString();
};

export interface ParsedQuery {
	/** Plain scalar string values, keyed by parameter name. */
	scalars: Record<string, string>;
	/** Repeated `key[]=…` values, keyed by parameter name (no trailing `[]`). */
	arrays: Record<string, string[]>;
	/** Dotted `key.subKey=…` values, keyed by parent key, then sub key. */
	nested: Record<string, Record<string, string>>;
}

/**
 * Keys that resolve to shared prototype internals. Using any of these as an
 * object key allows an attacker to walk into `Object.prototype` and pollute it,
 * so they are rejected wherever a query segment is used as a key.
 */
const UNSAFE_KEYS: ReadonlySet<string> = new Set(['__proto__', 'prototype', 'constructor']);

/**
 * Parse a query string (with or without leading `?`) into scalars, arrays and
 * dotted nested records. Mirrors the encoding done by `toQueryString`.
 */
export const fromQueryString = (search: string): ParsedQuery => {
	const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
	const result: ParsedQuery = {
		scalars: Object.create(null),
		arrays: Object.create(null),
		nested: Object.create(null),
	};
	params.forEach((value, rawKey) => {
		if (rawKey.endsWith('[]')) {
			const key = rawKey.slice(0, -2);
			if (UNSAFE_KEYS.has(key)) return;
			(result.arrays[key] ??= []).push(value);
		} else if (rawKey.includes('.')) {
			// Split on the LAST `.` so that multi-segment parents like
			// `product.jira` are preserved (parent = `product.jira`,
			// subKey = `units`).
			const lastDot = rawKey.lastIndexOf('.');
			const parent = rawKey.slice(0, lastDot);
			const subKey = rawKey.slice(lastDot + 1);
			if (UNSAFE_KEYS.has(parent) || UNSAFE_KEYS.has(subKey)) return;
			(result.nested[parent] ??= Object.create(null))[subKey] = value;
		} else {
			if (UNSAFE_KEYS.has(rawKey)) return;
			result.scalars[rawKey] = value;
		}
	});
	return result;
};
